import axios from "axios";
import { API_BASE_URL } from "../../config";

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("cms_token")}`,
});

// ==================== ENQUIRY LIST SERVICES ====================

/**
 * Fetch paginated enquiry list with filters
 */
export const fetchEnquiryList = async (page = 1, filterData = {}) => {
  try {
    const res = await axios.get(`${API_BASE_URL}/get-trademark-enquiries`, {
      params: { page, limit: 10, ...filterData },
      headers: getAuthHeaders(),
    });
    const payload = res?.data;
    if (payload?.status !== 200) throw new Error(payload?.message || "Failed to fetch enquiry list");
    return {
      data: Array.isArray(payload?.data) ? payload.data : [],
      total: Number(payload?.total_records ?? 0),
      pagination: payload?.pagination ?? {},
      totalPages: payload?.pagination?.total_pages ?? 1,
    };
  } catch (err) {
    throw new Error(err?.response?.data?.message || err?.message || "Failed to fetch enquiry list");
  }
};


// Fetch lead-details/trademark-enquiries details
export const fetchLeadDetails = async (id) => {
  const response = await axios.get(
    `${API_BASE_URL}/trademark-enquiries-details/${id}`,
    { headers: getAuthHeaders() }
  );
   if(response.data.status === 200) return response.data;
  throw new Error(response?.data?.message || "Failed to fetch lead/trademark details");
};


/**
 * Fetch single enquiry details by id
 */
export const fetchEnquiryDetails = async (id) => {
  const response = await axios.get(`${API_BASE_URL}/trademark-enquiries-details/${id}`, {
    headers: getAuthHeaders(),
  });
  if (response.data.status === 200) return response.data;
  throw new Error(response.data.message || "Failed to fetch enquiry details");
};

/**
 * Fetch internal user list with lead_limit info.
 * Used by both the Allocate modal and the "Assigned Admin" filter dropdown.
 *
 * Endpoint: GET /get-internal-user-list?page=1&limit=100
 */
export const fetchInternalUserList = async (page = 1, limit = 10) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/get-internal-user-list`, {
      params: { page, limit },
      headers: getAuthHeaders(),
    });
    if (response.data.status === 200) {
      const { data, total, pagination } = response.data;
      return {
        data: Array.isArray(data) ? data : [],
        total: total ?? 0,
        totalPages: pagination?.total_pages ?? 1,
      };
    }
    return { data: [], total: 0, totalPages: 1 };
  } catch {
    return { data: [], total: 0, totalPages: 1 };
  }
};

/**
 * Fetch enquiries for XLSX export (high limit, no pagination display)
 */
export const fetchExportEnquiries = async (filterData = {}) => {
  try {
    const res = await axios.get(`${API_BASE_URL}/get-trademark-enquiries`, {
      params: { ...filterData, is_positive: "", page: 1, limit: 500 },
      headers: getAuthHeaders(),
    });
    const payload = res?.data;
    if (payload?.status !== 200) throw new Error(payload?.message || "Failed to fetch export data");
    return Array.isArray(payload?.data) ? payload.data : [];
  } catch (err) {
    throw new Error(err?.response?.data?.message || err?.message || "Failed to fetch export data");
  }
};

// ==================== ENQUIRY MUTATION SERVICES ====================

/**
 * Delete one or multiple enquiries
 * @param {number|number[]} ids
 */
export const deleteEnquiry = async (ids) => {
  const idArray = Array.isArray(ids) ? ids : [ids];
  const response = await axios.post(
    `${API_BASE_URL}/delete-my-trademark-enquiry`,
    JSON.stringify({ id: idArray }),
    { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
  );
  if (response.data.status === 200) return response.data;
  throw new Error(response.data.message || "Failed to delete enquiry");
};

/**
 * Update lead contact details (name, email, phone, additional_phone)
 */
export const updateLeadContact = async (id, { name, email, phone, additional_phone }) => {
  const response = await axios.post(
    `${API_BASE_URL}/trademark-enquiries-update/${id}`,
    { name, email, phone, additional_phone },
    { headers: getAuthHeaders() }
  );
  if (response.data.status) return response.data;
  throw new Error(response.data.message || "Failed to update contact");
};

/**
 * Update enquiry status (positive / negative)
 * status: 1 = Positive, 2 = Negative
 */
export const updateEnquiryStatus = async ({ id, status, remarks = "" }) => {
  const response = await axios.post(
    `${API_BASE_URL}/is-positive-enquiry`,
    { id, status, remarks },
    { headers: getAuthHeaders() }
  );
  if (response.data.status === 200) return response.data;
  throw new Error(response.data.message || "Failed to update enquiry status");
};

/**
 * Import enquiries from an Excel file
 */
export const importEnquiries = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await axios.post(
    `${API_BASE_URL}/import-trademark-enquiry`,
    formData,
    { headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" } }
  );
  return response.data;
};

/**
 * Allocate selected leads to an internal user.
 *
 * Endpoint : POST /manual-asign-lead-to-user
 * Payload  : { internal_user_id: number, lead_ids: number[] }
 */
export const allocateLeads = async ({ internal_user_id, lead_ids }) => {
  let response;
  try {
    response = await axios.post(
      `${API_BASE_URL}/manual-asign-lead-to-user`,
      { internal_user_id, lead_ids },
      { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
    );
  } catch (err) {
    // The API puts the real reason in the body even on 404/422 — surface that
    // instead of axios's "Request failed with status code NNN".
    const data = err?.response?.data;
    throw new Error(
      data?.message || data?.error || err?.message || "Failed to allocate leads"
    );
  }
  if (response.data.status === 200) return response.data;
  throw new Error(response.data.message || "Failed to allocate leads");
};
